import { useSearchParams } from 'next/navigation'
import pluralize from 'pluralize'
import * as React from 'react'

import { ProgressiveAsset, Star } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { trackEvent } from '../../../lib/analytics'
import { useProductReviews } from '../../../lib/hooks/useProductReviews'
import { useProductReviewsHref } from '../../../lib/routing'

import {
  Container,
  Layout,
  ProductDetailsCopy,
  ReviewCountTextContainer,
  ReviewCountTextHeader,
  ReviewStarContainerHeader,
  ReviewStarIconContainerHeader,
  ReviewStars,
  Title,
  TitleContainer,
  TitleSection,
} from './styles'

export interface IProps {
  title: string
  description: string | undefined
  productHandle: string
  shopifyProductId: string
}

export const ReviewSection: React.FC<IProps> = ({
  title,
  description,
  productHandle,
  shopifyProductId,
}) => {
  // plain=true query param is used by iOS app to view webpages within a webview without the website's header/footer
  const plainQueryParamValue = useSearchParams()?.get('plain') ?? undefined
  const { detailsSection: detailsSectionCopy } = useTranslation()
  const { productReviews, productReviewsStatus } = useProductReviews({
    shopifyProductId,
  })

  const reviewsViewPath = useProductReviewsHref(productHandle, {
    plain: plainQueryParamValue,
  })

  const reviewTitleText: string =
    typeof productReviews?.totalReviews === 'number'
      ? `${productReviews.totalReviews.toLocaleString()} ${pluralize(
          detailsSectionCopy.reviewsCountNoun,
          productReviews.totalReviews
        )}`
      : detailsSectionCopy.writeReviewCTA

  const displayRating = (productReviews?.averageScore || 0).toFixed(2)

  const tooltip = `Rating of this product is ${displayRating} out of 5.`

  const titleAndDescription: string = title + ' ' + description

  const displaySkeletonUI = productReviewsStatus === 'pending' || productReviewsStatus === 'idle'

  return (
    <Layout>
      <Container>
        <TitleContainer>
          <TitleSection>
            <Title
              asTag='h1'
              {...trackEvent({
                category: 'product details',
                action: 'snapshot',
                label: 'title',
                value: title,
              })}
            >
              {titleAndDescription}
            </Title>
          </TitleSection>
        </TitleContainer>
        <ReviewStarContainerHeader data-testid='product-reviews' href={reviewsViewPath}>
          <ProgressiveAsset.Skeleton isVisible={displaySkeletonUI} />
          <ReviewStarIconContainerHeader isVisible={!displaySkeletonUI}>
            {productReviews?.averageScore === 0 || productReviews?.averageScore === null ? (
              Array.from({ length: 5 }, (_item, index: number) => (
                <Star key={index} height={16} width={16} />
              ))
            ) : (
              <ReviewStars
                rating={productReviews?.averageScore || 0}
                aria-label={tooltip}
                title={tooltip}
                isLarge
              />
            )}
          </ReviewStarIconContainerHeader>
          <ReviewCountTextContainer isVisible={!displaySkeletonUI}>
            <ProductDetailsCopy>
              <ReviewCountTextHeader>
                {' '}
                {productReviews?.averageScore === 0 || productReviews?.averageScore === null ? (
                  <u>{reviewTitleText}</u>
                ) : (
                  <span>
                    (<u>{reviewTitleText}</u>)
                  </span>
                )}
              </ReviewCountTextHeader>
            </ProductDetailsCopy>
          </ReviewCountTextContainer>
        </ReviewStarContainerHeader>
      </Container>
    </Layout>
  )
}
